import { computeCostModels } from '@ownup/fin-lib';
import React, { createContext, ReactNode, useContext, useReducer } from 'react';
import { creditTiers, interestRateToCreditTiers } from '../../common/constants';
import {
  fallbackAnnualIncome,
  fallbackDownPayment,
  maxAffordability,
  maxStretchingDTI,
  minStretchingDTI
} from '../constants';
import {
  AffordabilityDataActions,
  AffordabilityDataActionsTypes,
  AffordamilityState,
  CalculatorContextType
} from '../types';
import { getCostModels } from './costModels';
import { getAffordabilityRange, getMinAffordability } from './dtiData';
import { sliderGradient } from './sliderGetter';

const initialState: AffordamilityState = {
  annualIncome: 60000,
  monthlyDebt: 250,
  downPayment: 20000,
  propertyTaxRate: 1.2,
  hOAFees: 0,
  creditScore: creditTiers['740+'],
  interestRate: interestRateToCreditTiers[creditTiers['740+']],
  homePrice: 100800,
  monthlyPayment: 744,
  sliderValue: 0.3,
  showFull: false
};

function calculatorReducer(state: AffordamilityState, action: AffordabilityDataActions) {
  switch (action.type) {
    case AffordabilityDataActionsTypes.RESET_TO_DEFAULTS:
      return { ...initialState, showFull: state.showFull };
    case AffordabilityDataActionsTypes.SET_ANNUAL_INCOME:
      return { ...state, annualIncome: action.payload };
    case AffordabilityDataActionsTypes.SET_TOTAL_MONTHLY_DEBT:
      return { ...state, monthlyDebt: action.payload };
    case AffordabilityDataActionsTypes.SET_DOWN_PAYMENT:
      return { ...state, downPayment: action.payload };
    case AffordabilityDataActionsTypes.SET_CREDIT_SCORE:
      return {
        ...state,
        creditScore: action.payload,
        interestRate: interestRateToCreditTiers[action.payload]
      };
    case AffordabilityDataActionsTypes.SET_HOA_FEES:
      return { ...state, hOAFees: action.payload };
    case AffordabilityDataActionsTypes.SET_INTEREST_RATE:
      return { ...state, interestRate: action.payload };
    case AffordabilityDataActionsTypes.SET_PROPERTY_TAX_RATE:
      return { ...state, propertyTaxRate: action.payload };
    case AffordabilityDataActionsTypes.SET_SLIDER_VALUE:
      return { ...state, sliderValue: action.payload };
    case AffordabilityDataActionsTypes.SET_SHOW_FULL:
      return { ...state, showFull: action.payload };
    default:
      throw new Error(`Unhandled action type`);
  }
}

const CalculatorContext = createContext<CalculatorContextType>({
  state: initialState,
  dispatch: () => null,
  getDownPayment: () => 0,
  getAnnualIncome: () => 0,
  getMonthlyIncome: () => 0,
  getSliderGradient: () => ({
    maxStretch: 0,
    maxAffordable: 0
  }),
  getPresentValue: () => 0,
  getHomePrice: () => 0,
  getMonthlyPayment: () => 0,
  getResultDTISelector: () => 0,
  getAffordability: () => 'Affordable',
  getDownPaymentPercentage: () => 0,
  getError: () => ''
});

const roundTo = (value: number, percision: number) => Math.floor(value / percision) * percision;

export function CalculatorProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(calculatorReducer, initialState);

  const getDownPayment = () => state.downPayment ?? fallbackDownPayment;
  const getDownPaymentPercentage = () => {
    const homePrice = getHomePrice();
    const downPayment = state.downPayment;
    return downPayment / homePrice;
  };
  const getAnnualIncome = () => state.annualIncome ?? fallbackAnnualIncome;
  const getMonthlyIncome = () => getAnnualIncome() / 12;
  const getSliderGradient = () => sliderGradient(state, getMonthlyIncome());
  const getPresentValue = () => {
    const min = getMinAffordability(state, getMonthlyIncome());
    const range = getAffordabilityRange(state, getMonthlyIncome());
    const percent = state.sliderValue;
    const presentValue = min + range * percent;

    if (range >= 1_000_000) {
      return roundTo(presentValue, 10_000);
    }
    if (range >= 500_000) {
      return roundTo(presentValue, 1_000);
    }
    if (range >= 100_000) {
      return roundTo(presentValue, 100);
    }
    if (range >= 10_000) {
      return roundTo(presentValue, 10);
    }
    return roundTo(presentValue, 1);
  };
  const getHomePrice = () => getPresentValue() + getDownPayment();

  const getMonthlyPayment = () =>
    Math.ceil(
      Object.values(
        computeCostModels({ presentValue: getPresentValue(), costs: getCostModels(state) })
      ).reduce((l, r) => l + r, 0)
    );
  const getResultDTISelector = () => {
    const monthlyPayment = getMonthlyPayment();
    const monthlyDebt = state.monthlyDebt;
    const monthlyIncome = getMonthlyIncome();
    return (monthlyPayment + monthlyDebt) / monthlyIncome;
  };
  const getAffordability = () => {
    const dti = getResultDTISelector();
    if (dti < minStretchingDTI) {
      return 'Affordable';
    }
    if (dti <= maxStretchingDTI) {
      return 'Stretching';
    }
    return 'Aggressive';
  };
  const getError = () => {
    const homePrice = getHomePrice();
    const monthlyPayment = getMonthlyPayment();
    const monthlyDebt = state.monthlyDebt;
    const monthlyIncome = getMonthlyIncome();
    if (!Number.isNaN(homePrice) && !Number.isNaN(monthlyPayment)) {
      // If both homePrice and monthlyPayment are numbers, no error
      return '';
    }

    // Try to narrow down the cause to show a specific message
    // Comparing to maxStretchingDTI because the monthly payment factors in
    // and error will occur before reaching 50%
    if (monthlyDebt / monthlyIncome > maxStretchingDTI) {
      return `Your DTI is too high (>${Math.round(
        maxAffordability * 100
      )}%) try adjusting your annual income or monthly debts.`;
    }

    return `Your DTI is too high (>${Math.round(
      maxAffordability * 100
    )}%) try adjusting your property tax and/or your HOA fees.`;
  };

  const value = {
    state,
    dispatch,
    getDownPayment,
    getDownPaymentPercentage,
    getAnnualIncome,
    getMonthlyIncome,
    getSliderGradient,
    getPresentValue,
    getHomePrice,
    getMonthlyPayment,
    getResultDTISelector,
    getAffordability,
    getError
  };

  return <CalculatorContext.Provider value={value}>{children}</CalculatorContext.Provider>;
}

export function useCalculator() {
  const context = useContext(CalculatorContext);
  if (context === undefined) {
    throw new Error('useCalculator must be used within a CalculatorProvider');
  }
  return context;
}
